import _isNumber from "lodash/isNumber";
import moment from "moment";
import TRANSFER_TEXT from "./text-consts";
import { BoxData, ExistingAgreement, ExistingService, ExistingSubscription, TableData } from "../../../models/transfer";
import { getFormattedPrice } from "../../../shared/priceFormatter";
import { PhoneNumberFormatters } from "../app/utils";

const formatter = PhoneNumberFormatters[0].displayFn;
const DATE_FORMAT_DDMMYYYY_WITH_SEPARATION = (separator: string) => ["DD", "MM", "YYYY"].join(separator);

export function convertSubscriptionToTableData(
    subscription: ExistingSubscription,
    showNewOwner: boolean,
    showExistingOwner: boolean,
    isCreditCheckDenied: boolean,
): TableData {
    const {
        phoneNumber,
        newLegalOwner,
        legalOwner,
        productUser,
        subscriptionName,
        subscriptionPrice,
        commitmentEndDate,
    } = subscription;

    const leaseTime = commitmentEndDate
        ? moment(commitmentEndDate).format(DATE_FORMAT_DDMMYYYY_WITH_SEPARATION("."))
        : TRANSFER_TEXT.SUMMARY.SUBSCRIPTION_INFORMATION.NO_LEASETIME;

    const subscriptionInformation = [
        {
            label: TRANSFER_TEXT.SUMMARY.SUBSCRIPTION_INFORMATION.NUMBER,
            value: formatter(phoneNumber),
        },
        ...(showExistingOwner
            ? [
                  {
                      label: TRANSFER_TEXT.SUMMARY.SUBSCRIPTION_INFORMATION.EXISTING_LEGALOWNER,
                      value: legalOwner,
                  },
                  {
                      label: TRANSFER_TEXT.SUMMARY.SUBSCRIPTION_INFORMATION.EXISTING_PRODUCTUSER,
                      value: productUser,
                  },
              ]
            : []),
        ...(showNewOwner
            ? [
                  {
                      label: TRANSFER_TEXT.SUMMARY.SUBSCRIPTION_INFORMATION.NEW_LEGALOWNER,
                      value: newLegalOwner,
                  },
              ]
            : []),
        {
            label: TRANSFER_TEXT.SUMMARY.SUBSCRIPTION_INFORMATION.SUBSCRIPTION,
            value: subscriptionName,
            strikethrough: isCreditCheckDenied,
        },
        {
            label: TRANSFER_TEXT.SUMMARY.SUBSCRIPTION_INFORMATION.PRICE,
            value: _isNumber(subscriptionPrice)
                ? `${getFormattedPrice(subscriptionPrice)} ${TRANSFER_TEXT.SUMMARY.SUBSCRIPTION_INFORMATION.PER_MONTH}`
                : "N/A",
            strikethrough: isCreditCheckDenied,
        },
        {
            label: TRANSFER_TEXT.SUMMARY.SUBSCRIPTION_INFORMATION.END_DATE,
            value: leaseTime,
            strikethrough: isCreditCheckDenied,
        },
    ];

    return subscriptionInformation;
}

export const isSwitchAgreement = (agreementType: string) => agreementType.includes("SWITCH");

export const isSwitchPlusInsurance = (insuranceType?: string) =>
    insuranceType && insuranceType.includes("INSURANCE_SWITCH_4_EXISTING");

export const SCREEN_INSURANCE = {
    product: "Skjermforsikring",
    price: {
        monthly: 0,
    },
};

export const getKeepAgreementDescription = (agreementType: string, insuranceType?: string, newLegalOwner?: string) => {
    if (!isSwitchAgreement(agreementType)) {
        return TRANSFER_TEXT.LANDING_PAGE.LEASE_KEEP_AGREEMENT_DESCRIPTION(newLegalOwner);
    }

    const switchDescription = TRANSFER_TEXT.LANDING_PAGE.SWITCH_KEEP_AGREEMENT_DESCRIPTION;
    const switchPlusInsuranceDescription = TRANSFER_TEXT.LANDING_PAGE.SWITCH_KEEP_AGREEMENT_PLUS_INSURANCE;

    return isSwitchPlusInsurance(insuranceType)
        ? switchDescription + switchPlusInsuranceDescription
        : switchDescription;
};

export function convertAgreementToBoxData({
    product,
    price,
    commitmentEndDate,
    totalRemainingCost,
    totalRemainingMonths,
    model,
    type,
    imei,
}: ExistingAgreement): BoxData {
    const leaseTime = commitmentEndDate
        ? moment(commitmentEndDate).format(DATE_FORMAT_DDMMYYYY_WITH_SEPARATION("."))
        : TRANSFER_TEXT.SUMMARY.RELATED_INFORMATION.NO_LEASETIME;

    return {
        icon: isSwitchAgreement(type) ? "trade-phone" : "smartphone",
        title: product,
        model,
        imei: imei,
        price: price.monthly,
        data: [
            {
                label: TRANSFER_TEXT.SUMMARY.RELATED_INFORMATION.END_DATE,
                value: leaseTime,
            },
            {
                label: TRANSFER_TEXT.SUMMARY.RELATED_INFORMATION.REMAINING_MONTHS,
                value: totalRemainingMonths,
            },
            {
                label: TRANSFER_TEXT.SUMMARY.RELATED_INFORMATION.REMAINING_COST,
                value: getFormattedPrice(totalRemainingCost),
            },
        ],
    };
}

export function convertServiceToBoxData(service: ExistingService) {
    const { product, price, commitmentStartDate, commitmentEndDate, simNumber } = service;

    const leaseTime =
        commitmentStartDate && commitmentEndDate
            ? `${moment(commitmentStartDate).format(DATE_FORMAT_DDMMYYYY_WITH_SEPARATION("."))} - ${moment(
                  commitmentEndDate,
              ).format(DATE_FORMAT_DDMMYYYY_WITH_SEPARATION("."))}`
            : TRANSFER_TEXT.SUMMARY.RELATED_INFORMATION.NO_LEASETIME;

    const serviceInformation = [`${TRANSFER_TEXT.SUMMARY.RELATED_INFORMATION.LEASE_TIME}: ${leaseTime}`];

    if (simNumber) {
        serviceInformation.push(`${TRANSFER_TEXT.SUMMARY.RELATED_INFORMATION.SIM_NUMBER}: ${simNumber}`);
    }

    return {
        icon: simNumber ? "sim-card" : "insurance",
        title: product,
        info: serviceInformation,
        price: price.monthly,
    };
}
